/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: prop-types library.
import InfiniteScrollComponent from 'react-infinite-scroll-component' // Npm: Infinite scrolling.
import ReactList from 'react-list' // Npm: Virtual list rendering.
import { Box, Text } from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * OBJECTS
 */
const Index = React.forwardRef(({
  id,
  items,
  hasMore,
  loading,
  error,
  loadMore,
  renderItem,
  ariaLabel = 'Options',
  height = 260,
  itemHeight = 40,
  pageSize = 10,
  scrollThreshold = 0.85
}, ref) => {
  // Hook assignment.
  const [_canLoadMore, _setCanLoadMore] = React.useState(true)
  const _scrollRef = React.useRef(null)
  const _virtualListRef = React.useRef(null)

  // Load one page for each user scroll to the end of the list.
  const _LoadMore = React.useCallback(() => {
    if (!_canLoadMore || loading || error || !hasMore) return void 0

    _setCanLoadMore(false)
    return loadMore()
  }, [_canLoadMore, error, hasMore, loadMore, loading])

  // Re-arm loading only after another physical scroll action.
  const _HandleScroll = React.useCallback(event => {
    if (!event.nativeEvent.isTrusted || loading || _canLoadMore) return
    _setCanLoadMore(true)
  }, [_canLoadMore, loading])

  // Expose navigation without leaking implementation details to selectors.
  React.useImperativeHandle(ref, () => ({
    'loadMore': _LoadMore,
    'scrollAround': index => _virtualListRef.current?.scrollAround(index),
    'scrollToTop': () => {
      if (_scrollRef.current) _scrollRef.current.scrollTop = 0
      _setCanLoadMore(true)
    }
  }), [_LoadMore])

  // Return component.
  return (
    <Box
      ref={_scrollRef}
      id={id}
      role='listbox'
      aria-label={ariaLabel}
      maxH={`${height}px`}
      overflowY='auto'
      mt={2}
      onScroll={_HandleScroll}>
      <InfiniteScrollComponent
        dataLength={items.length}
        next={_LoadMore}
        hasMore={_canLoadMore && hasMore && !error}
        loader={<Text px={3} py={2} color='#A3AED0' fontFamily='inherit' fontSize='13px' fontWeight={400}>Loading...</Text>}
        scrollableTarget={id}
        scrollThreshold={scrollThreshold}
        style={{ 'overflow': 'visible' }}>
        <ReactList
          ref={_virtualListRef}
          length={items.length}
          itemRenderer={renderItem}
          itemSizeEstimator={() => itemHeight}
          pageSize={pageSize}
          type='uniform'
          useStaticSize />
      </InfiniteScrollComponent>
    </Box>
  )
})


/*
 * PROPTYPES
 */
Index.displayName = 'InfiniteScroll'
Index.propTypes = {
  'id': PropTypes.string.isRequired,
  'items': PropTypes.array.isRequired,
  'hasMore': PropTypes.bool.isRequired,
  'loading': PropTypes.bool,
  'error': PropTypes.string,
  'loadMore': PropTypes.func.isRequired,
  'renderItem': PropTypes.func.isRequired,
  'ariaLabel': PropTypes.string,
  'height': PropTypes.number,
  'itemHeight': PropTypes.number,
  'pageSize': PropTypes.number,
  'scrollThreshold': PropTypes.number
}


/*
 * EXPORT
 */
export default React.memo(Index)
